// Configuration for the portrait "kiosk" menu layout.
//
// The Rista menu is a clean two-level tree (parent category -> subcategory ->
// product), but several parent "categories" are actually customization
// option-sets (grind size, pour-over choice, add-ons…) that get attached to a
// product via optionSetIds. They must never appear as browsable categories in
// the kiosk rail — they surface only inside the product customization sheet.

// Parent-category names that are option-sets, not menu sections.
// Matched case-insensitively against a substring of the category name.
export const OPTION_SET_CATEGORY_PATTERNS = [
  "addon",
  "grind option",
  "pour over option",
  "pour-over option",
  "bread option",
  "manual brew option",
];

// Take-home / retail categories (beans, packaged goods, merch, equipment).
// Everything not matched here is treated as "order now" (drinks, food, bakery).
export const TAKE_HOME_CATEGORY_PATTERNS = [
  "roasted coffee",
  "packed treats",
  "retail",
  "merchandise",
  "merch",
  "equipment",
  "beans",
];

export const KIOSK_GROUPS = {
  now: "Categories",
  home: "Take home",
};


const matchesAny = (name, patterns) => {
  const lower = (name || "").toLowerCase();
  return patterns.some((p) => lower.includes(p));
};

export const isOptionSetCategory = (category) =>
  matchesAny(category?.parentName, OPTION_SET_CATEGORY_PATTERNS);

export const getCategoryGroup = (category) =>
  matchesAny(category?.parentName, TAKE_HOME_CATEGORY_PATTERNS) ? "home" : "now";

// Whether the kiosk layout should be active for this session.
// Explicit and opt-in: only the ?mode=kiosk URL flag turns it on, so the
// existing mobile/desktop experience is completely untouched by default.
export const isKioskMode = () => {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  return (params.get("mode") || "").toLowerCase() === "kiosk";
};

// A short emoji glyph for a category, chosen by name. Purely decorative — the
// rail still shows the category name as its label.
const CATEGORY_ICONS = [
  [["all"], "◉"],
  [["brewed", "coffee", "espresso"], "☕"],
  [["cold"], "🥤"],
  [["tea", "beverage", "kombucha", "cooler", "juice"], "🧋"],
  [["food", "breakfast", "sandwich", "pasta", "wrap"], "🍽️"],
  [["bake", "bread", "croissant", "cookie", "pastry", "suchali"], "🥐"],
  [["roasted", "bean"], "🫘"],
  [["treat", "chocolate", "snack"], "🍫"],
  [["merch", "sticker", "cup", "tote"], "🧢"],
  [["equipment", "brewing"], "⚗️"],
  [["water", "can"], "🥫"],
];

export const getCategoryIcon = (category) => {
  const name = (category?.parentName || "").toLowerCase();
  for (const [keys, icon] of CATEGORY_ICONS) {
    if (keys.some((k) => name.includes(k))) return icon;
  }
  return "•";
};

// Build a synthetic "All" category that aggregates every product from every
// browse category, deduped by id/skuCode/name, preserving category order.
export const buildAllCategory = (browseCategories) => {
  const seen = new Set();
  const products = [];

  browseCategories.forEach((cat) => {
    (cat.products || []).forEach((p) => {
      const key = p?.id || p?.skuCode || p?.name;
      if (p && key && !seen.has(key)) {
        seen.add(key);
        products.push(p);
      }
    });
  });

  if (products.length === 0) return null;

  return {
    id: "__all__",
    parentName: "All",
    description: "Our full menu",
    subCategories: [],
    products,
    __isAll: true,
  };
};
